import { memo, useCallback, useEffect } from "react"
import ApiFetchers from "@/apis/api_fetchers"
import useTransactionList from "@/features_signal/transactionRecord/transaction_list"
import Amount from "@/pages/TransactionRecordPage/components/Amount"
import DateText from "@/pages/TransactionRecordPage/components/DateText"
import ItemTitle from "@/pages/TransactionRecordPage/components/ItemTitle"
import {
    ItemWrapper,
    Left as LeftWrapper,
    Right as RightWrapper,
} from "@/pages/TransactionRecordPage/components/Wrappers"
import styled from "@emotion/styled"
import { useSearchParams } from "react-router-dom"
import BaseRecordList from "../components/BaseRecordList"
import { ALL, ALL_TEXT } from "../constants"
import { BaseFormType } from "../types"
import { deduplicationList, getFormData, getURLSearchParams } from "../utils/util"
import FootDataCount from "@/pages/TransactionRecordPage/components/FootDataCount"
import { Virtuoso } from "react-virtuoso"
import {
    fetchTransactionTypes,
    transactionTypes,
} from "@/features_signal/transactionRecord/transaction_types"
import { formatCurrency } from "@/utils/format"

const BalanceStyled = styled.div`
    font-size: 12rem;
    color: #69728c;
`

const Component: React.FC = () => {
    const [params, setParams] = useSearchParams()

    const { list, loading, fetchData, hasMore, error } = useTransactionList(
        ApiFetchers.getTransactionRecords,
        {
            ...getFormData({ ...getURLSearchParams(params) }),
        },
    )

    const handleChange = useCallback(
        (value?: BaseFormType) => {
            setParams(value ? { ...value } : {}, { replace: true })
        },
        [setParams],
    )

    const uniqueList = deduplicationList<TransactionApiTypes.Fetcher.Response.Record>(list) || []

    const loadMoreDebounced = () => {
        if (!hasMore) return
        if (loading) return
        fetchData()
    }

    const getBalanceStatus = () => {
        const balanceStatus = transactionTypes?.value?.map((item) => ({
            name: item.name || "",
            value: `${item.cash_type}` || "",
        }))
        balanceStatus?.unshift({
            name: ALL_TEXT,
            value: ALL,
        })
        return balanceStatus
    }

    useEffect(() => {
        fetchTransactionTypes()
    }, [])

    return (
        <>
            <BaseRecordList<TransactionApiTypes.Fetcher.Response.Record>
                value={getURLSearchParams(params)}
                statusOptions={getBalanceStatus()}
                onChange={handleChange}
                list={uniqueList}
                loading={loading}
                error={error}
                hasMore={hasMore}
                selectTitle="Select Type"
            >
                <Virtuoso
                    style={{ flex: 1 }}
                    data={uniqueList}
                    endReached={loadMoreDebounced}
                    components={{
                        Footer: () => (
                            <FootDataCount
                                loading={loading}
                                hasMore={hasMore}
                                count={list.length}
                            />
                        ),
                    }}
                    itemContent={(_, item) => (
                        <ItemWrapper key={item.id}>
                            <LeftWrapper>
                                <ItemTitle text={item.name} />
                                <DateText timestamp={item.timestamp} />
                            </LeftWrapper>
                            <RightWrapper>
                                <Amount
                                    amount={item.amount}
                                    isColorFulled
                                />
                                <BalanceStyled>
                                    Balance:{" "}
                                    {formatCurrency(
                                        item.after_balance ? Number(item.after_balance) : 0,
                                    )}
                                </BalanceStyled>
                            </RightWrapper>
                        </ItemWrapper>
                    )}
                />
            </BaseRecordList>
        </>
    )
}

export default memo(Component)
